import { BUILDING_TYPES, RESOURCE_ORDER, TUNING, UNIT_TYPES } from './definitions.js'
import { findPath } from './Navigation.js'

const distance = (_a, _b) => Math.hypot(_a.x - _b.x, _a.y - _b.y)

export default class LogisticsSystem
{
    constructor(_state)
    {
        this.state = _state
    }

    update(_delta)
    {
        for(const unit of this.state.units)
        {
            if(!unit.alive)
                continue
            if(unit.role === 'miner' || unit.role === 'woodcutter')
                this.updateHarvester(unit, _delta)
            else if(unit.role === 'porter')
                this.updatePorter(unit, _delta)
        }
    }

    obstacles()
    {
        const obstacles = []
        for(const deposit of this.state.deposits)
        {
            if(deposit.revealed && deposit.remaining > 0)
                obstacles.push({ x: deposit.x, y: deposit.y, radius: deposit.tree ? 0.65 : 2.15 })
        }
        for(const building of this.state.buildings)
            obstacles.push({ x: building.x, y: building.y, radius: 1.35 })
        if(this.state.ossuary.revealed)
            obstacles.push({ x: this.state.ossuary.x, y: this.state.ossuary.y, radius: 1.3 })
        return obstacles
    }

    setDestination(_unit, _target)
    {
        _unit.destination = { x: _target.x, y: _target.y }
        _unit.path = findPath(_unit, _target, this.obstacles())
        _unit.pathIndex = 0
        if(!_unit.path.length && distance(_unit, _target) < 0.65)
            _unit.destination = null
    }

    followPath(_unit, _speed, _delta)
    {
        if(!_unit.destination)
            return true
        const waypoint = _unit.path?.[_unit.pathIndex] ?? _unit.destination
        const remaining = distance(_unit, waypoint)
        const step = Math.min(remaining, _speed * _delta)
        if(remaining > 0.001)
        {
            _unit.x += (waypoint.x - _unit.x) / remaining * step
            _unit.y += (waypoint.y - _unit.y) / remaining * step
            _unit.rotation = Math.atan2(- (waypoint.x - _unit.x), waypoint.y - _unit.y)
        }
        if(remaining > 0.15)
            return false
        if(_unit.path && _unit.pathIndex < _unit.path.length - 1)
        {
            _unit.pathIndex++
            return false
        }
        _unit.destination = null
        _unit.path = []
        return true
    }

    depositApproach(_deposit, _unit)
    {
        const offsetX = _unit.x - _deposit.x
        const offsetY = _unit.y - _deposit.y
        const length = Math.max(0.001, Math.hypot(offsetX, offsetY))
        const radius = _deposit.tree ? 1.05 : 2.75
        return { x: _deposit.x + offsetX / length * radius, y: _deposit.y + offsetY / length * radius }
    }

    buildingApproach(_building)
    {
        return { x: _building.x, y: _building.y - 2.05 }
    }

    assignHarvester(_unit)
    {
        const candidates = this.state.deposits.filter((_deposit) =>
        {
            if(!_deposit.revealed || Boolean(_deposit.tree) !== (_unit.role === 'woodcutter'))
                return false
            const assigned = this.state.units.filter((_item) => _item.alive && _item.assignmentId === _deposit.id).length
            return _unit.role === 'woodcutter' ? assigned === 0 : assigned < 2
        })
        candidates.sort((_a, _b) =>
        {
            const assignedA = this.state.units.filter((_item) => _item.alive && _item.assignmentId === _a.id).length
            const assignedB = this.state.units.filter((_item) => _item.alive && _item.assignmentId === _b.id).length
            return assignedA - assignedB || distance(_unit, _a) - distance(_unit, _b) || _a.id.localeCompare(_b.id)
        })
        const deposit = candidates[0]
        if(!deposit)
            return false
        _unit.assignmentId = deposit.id
        _unit.workCooldown = 0
        this.setDestination(_unit, this.depositApproach(deposit, _unit))
        return true
    }

    updateHarvester(_unit, _delta)
    {
        const definition = UNIT_TYPES[_unit.type]
        if(!_unit.assignmentId && !this.assignHarvester(_unit))
            return
        const deposit = this.state.deposits.find((_item) => _item.id === _unit.assignmentId)
        if(!deposit)
        {
            _unit.assignmentId = null
            return
        }
        if(!this.followPath(_unit, definition.speed * this.state.workerMultiplier, _delta))
            return
        _unit.workCooldown = Math.max(0, _unit.workCooldown - _delta)
        if(_unit.workCooldown > 0 || deposit.remaining <= 0 || deposit.output >= TUNING.outputCapacity)
            return
        deposit.remaining--
        deposit.output++
        _unit.workCooldown = TUNING.workerGatherInterval / this.state.workerMultiplier
        if(deposit.remaining === 0)
            deposit.regrowAt = this.state.elapsed + TUNING.sourceRegrowSeconds
        this.state.trigger('unitWorked', [{ unit: _unit, deposit, resource: deposit.resource }])
        this.state.trigger('depositChanged', [deposit])
    }

    sourcePosition(_source, _unit)
    {
        if(_source.kind === 'deposit')
        {
            const deposit = this.state.deposits.find((_item) => _item.id === _source.id)
            return deposit ? this.depositApproach(deposit, _unit) : null
        }
        if(_source.kind === 'building')
        {
            const building = this.state.buildings.find((_item) => _item.id === _source.id)
            return building ? this.buildingApproach(building) : null
        }
        return { x: this.state.ossuary.x, y: this.state.ossuary.y - 2 }
    }

    destinationPosition(_destination)
    {
        if(_destination.kind === 'site')
        {
            const site = this.state.sites.find((_item) => _item.id === _destination.id)
            return site ? { x: site.x, y: site.y } : null
        }
        if(_destination.kind === 'building')
        {
            const building = this.state.buildings.find((_item) => _item.id === _destination.id)
            return building ? this.buildingApproach(building) : null
        }
        return { x: this.state.ossuary.x, y: this.state.ossuary.y - 2 }
    }

    sourceAmount(_source, _resource)
    {
        if(_source.kind === 'deposit')
        {
            const deposit = this.state.deposits.find((_item) => _item.id === _source.id)
            return deposit?.resource === _resource ? deposit.output : 0
        }
        if(_source.kind === 'building')
        {
            const building = this.state.buildings.find((_item) => _item.id === _source.id)
            const definition = building ? BUILDING_TYPES[building.type] : null
            return definition?.output === _resource ? building.output : 0
        }
        return this.state.ossuary.stored[_resource] ?? 0
    }

    reservedSource(_source, _resource)
    {
        return this.state.units
            .filter((_unit) => _unit.role === 'porter' && _unit.task?.stage === 'toSource' && _unit.task.source.kind === _source.kind && _unit.task.source.id === _source.id && _unit.task.resource === _resource)
            .reduce((_total, _unit) => _total + _unit.task.count, 0)
    }

    reservedDestination(_destination, _resource)
    {
        return this.state.units
            .filter((_unit) => _unit.role === 'porter' && _unit.task && _unit.task.destination.kind === _destination.kind && _unit.task.destination.id === _destination.id && _unit.task.resource === _resource)
            .reduce((_total, _unit) => _total + _unit.task.count, 0)
    }

    cryptDemands(_building, _priority)
    {
        const definition = BUILDING_TYPES[_building.type]
        const availableTypes = definition.units.filter((_type) => this.state.unitCount(_type) < UNIT_TYPES[_type].cap)
        if(!availableTypes.length)
            return []
        const demands = []
        for(const type of availableTypes)
        {
            const missing = UNIT_TYPES[type].cap - this.state.unitCount(type)
            for(const [resource, recipeCount] of Object.entries(UNIT_TYPES[type].recipe))
            {
                const destination = { kind: 'building', id: _building.id }
                const wanted = Math.min(TUNING.machineSlotCapacity, recipeCount * Math.min(2, missing))
                const remaining = wanted - (_building.inputs[resource] ?? 0) - this.reservedDestination(destination, resource)
                if(remaining > 0 && !demands.some((_item) => _item.resource === resource && _item.destination.id === _building.id))
                    demands.push({ destination, resource, amount: remaining, priority: _priority, x: _building.x, y: _building.y })
            }
        }
        return demands
    }

    demands()
    {
        const demands = []
        const site = this.state.activeSite
        if(site)
        {
            for(const [resource, required] of Object.entries(site.recipe))
            {
                const destination = { kind: 'site', id: site.id }
                const amount = required - site.deposited[resource] - this.reservedDestination(destination, resource)
                if(amount > 0)
                    demands.push({ destination, resource, amount, priority: 1, x: site.x, y: site.y })
            }
        }
        for(const building of this.state.buildings)
        {
            const definition = BUILDING_TYPES[building.type]
            if(definition.kind === 'crypt')
            {
                const combat = definition.units.some((_type) => UNIT_TYPES[_type].role === 'combat')
                demands.push(...this.cryptDemands(building, combat ? 2 : 3))
                continue
            }
            const destination = { kind: 'building', id: building.id }
            const inputAmount = TUNING.machineSlotCapacity - (building.inputs[definition.input] ?? 0) - this.reservedDestination(destination, definition.input)
            if(inputAmount > 0)
                demands.push({ destination, resource: definition.input, amount: inputAmount, priority: 4, x: building.x, y: building.y })
            if(definition.fuel)
            {
                const fuelStored = (building.inputs.souls ?? 0) + (building.inputs.deadwood ?? 0)
                for(const resource of ['souls', 'deadwood'])
                {
                    const amount = TUNING.machineSlotCapacity - fuelStored - (building.inputs[resource] ?? 0) - this.reservedDestination(destination, resource)
                    if(amount > 0)
                        demands.push({ destination, resource, amount, priority: 4, x: building.x, y: building.y })
                }
            }
        }
        if(this.state.ossuary.revealed)
        {
            for(const resource of RESOURCE_ORDER)
                demands.push({ destination: { kind: 'ossuary', id: 'ossuary' }, resource, amount: Infinity, priority: 5, x: this.state.ossuary.x, y: this.state.ossuary.y })
        }
        return demands
    }

    sources(_resource)
    {
        const sources = []
        for(const deposit of this.state.deposits)
        {
            if(deposit.revealed && deposit.resource === _resource)
                sources.push({ kind: 'deposit', id: deposit.id, x: deposit.x, y: deposit.y })
        }
        for(const building of this.state.buildings)
        {
            if(BUILDING_TYPES[building.type].output === _resource)
                sources.push({ kind: 'building', id: building.id, x: building.x, y: building.y })
        }
        if(this.state.ossuary.revealed)
            sources.push({ kind: 'ossuary', id: 'ossuary', x: this.state.ossuary.x, y: this.state.ossuary.y })
        return sources
    }

    assignPorter(_unit)
    {
        const demands = this.demands().sort((_a, _b) => _a.priority - _b.priority || RESOURCE_ORDER.indexOf(_a.resource) - RESOURCE_ORDER.indexOf(_b.resource))
        for(const demand of demands)
        {
            const sources = this.sources(demand.resource)
                .filter((_source) => !(demand.destination.kind === 'ossuary' && _source.kind === 'ossuary'))
                .sort((_a, _b) => distance(_unit, _a) - distance(_unit, _b))
            for(const source of sources)
            {
                const available = this.sourceAmount(source, demand.resource) - this.reservedSource(source, demand.resource)
                if(available <= 0)
                    continue
                const count = Math.min(TUNING.porterCapacity, available, demand.amount)
                if(count <= 0)
                    continue
                const target = this.sourcePosition(source, _unit)
                if(!target)
                    continue
                _unit.task = { source, destination: demand.destination, resource: demand.resource, count, stage: 'toSource' }
                this.setDestination(_unit, target)
                return true
            }
        }
        return false
    }

    takeFromSource(_source, _resource, _count)
    {
        if(_source.kind === 'deposit')
        {
            const deposit = this.state.deposits.find((_item) => _item.id === _source.id)
            if(!deposit || deposit.resource !== _resource)
                return 0
            const count = Math.min(_count, deposit.output)
            deposit.output -= count
            this.state.trigger('depositChanged', [deposit])
            return count
        }
        if(_source.kind === 'building')
        {
            const building = this.state.buildings.find((_item) => _item.id === _source.id)
            if(!building || BUILDING_TYPES[building.type].output !== _resource)
                return 0
            const count = Math.min(_count, building.output)
            building.output -= count
            this.state.trigger('buildingChanged', [building])
            return count
        }
        const count = Math.min(_count, this.state.ossuary.stored[_resource] ?? 0)
        this.state.ossuary.stored[_resource] -= count
        this.state.trigger('ossuaryChanged', [this.state.ossuary])
        return count
    }

    deliver(_task, _count)
    {
        if(_task.destination.kind === 'site')
        {
            const site = this.state.sites.find((_item) => _item.id === _task.destination.id)
            if(site && !site.built)
            {
                const accepted = Math.min(_count, site.recipe[_task.resource] - site.deposited[_task.resource])
                site.deposited[_task.resource] += accepted
                this.state.trigger('siteTransfer', [{ site, resource: _task.resource, count: accepted, porter: true }])
                this.state.checkSiteCompletion(site)
                return accepted
            }
            return 0
        }
        if(_task.destination.kind === 'building')
        {
            const building = this.state.buildings.find((_item) => _item.id === _task.destination.id)
            if(building && this.state.getAcceptedResources(building).includes(_task.resource))
            {
                const fuel = _task.resource === 'souls' || _task.resource === 'deadwood'
                const stored = fuel ? (building.inputs.souls ?? 0) + (building.inputs.deadwood ?? 0) : (building.inputs[_task.resource] ?? 0)
                const accepted = Math.min(_count, TUNING.machineSlotCapacity - stored)
                building.inputs[_task.resource] = (building.inputs[_task.resource] ?? 0) + accepted
                this.state.trigger('buildingInput', [{ building, resource: _task.resource, count: accepted, porter: true }])
                return accepted
            }
            return 0
        }
        this.state.ossuary.stored[_task.resource] = (this.state.ossuary.stored[_task.resource] ?? 0) + _count
        this.state.trigger('ossuaryChanged', [this.state.ossuary])
        return _count
    }

    updatePorter(_unit, _delta)
    {
        if(!_unit.task && !this.assignPorter(_unit))
            return
        if(!this.followPath(_unit, UNIT_TYPES.porter.speed * this.state.workerMultiplier, _delta))
            return
        const task = _unit.task
        if(task.stage === 'toSource')
        {
            const count = this.takeFromSource(task.source, task.resource, task.count)
            task.count = count
            if(count <= 0)
            {
                _unit.task = null
                return
            }
            _unit.carry = { resource: task.resource, count }
            task.stage = 'toDestination'
            const target = this.destinationPosition(task.destination)
            if(!target)
            {
                task.destination = { kind: 'ossuary', id: 'ossuary' }
                this.setDestination(_unit, { x: this.state.ossuary.x, y: this.state.ossuary.y - 2 })
            }
            else
                this.setDestination(_unit, target)
            this.state.trigger('porterCollected', [{ unit: _unit, resource: task.resource, count }])
            return
        }

        const accepted = this.deliver(task, task.count)
        const remaining = task.count - accepted
        if(remaining > 0 && this.state.ossuary.revealed && task.destination.kind !== 'ossuary')
        {
            this.state.ossuary.stored[task.resource] = (this.state.ossuary.stored[task.resource] ?? 0) + remaining
            this.state.trigger('ossuaryChanged', [this.state.ossuary])
        }
        this.state.trigger('porterDelivered', [{ unit: _unit, resource: task.resource, count: accepted }])
        _unit.carry = null
        _unit.task = null
    }
}
